/**
 * Project Hooks
 * React Query hooks for project CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { showToast } from '@/components/Notifications/Toast'
import type { Project, CreateProjectDto, UpdateProjectDto } from '@fmeastudio/shared'
import {
  getProjects,
  getProject,
  createProject,
  updateProject,
  archiveProject,
  deleteProject,
  duplicateProject,
} from '@/api/projects'

// Get all projects
export function useProjects(includeArchived = false) {
  return useQuery({
    queryKey: ['projects', includeArchived],
    queryFn: () => getProjects(includeArchived),
  })
}

// Get single project by ID
export function useProject(id: string) {
  return useQuery({
    queryKey: ['projects', id],
    queryFn: () => getProject(id),
    enabled: !!id,
  })
}

// Create new project
export function useCreateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateProjectDto) => createProject(data),
    onSuccess: async () => {
      // Use refetchQueries() to wait for fresh data before UI updates
      await queryClient.refetchQueries({ queryKey: ['projects'] })
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to create project')
    },
  })
}

// Update existing project
export function useUpdateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectDto }) =>
      updateProject(id, data),
    onSuccess: async (_, variables) => {
      // Use refetchQueries() to wait for fresh data before UI updates
      await queryClient.refetchQueries({ queryKey: ['projects', variables.id] })
      await queryClient.refetchQueries({ queryKey: ['projects'] })
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to update project')
    },
  })
}

// Archive project (sets status to ARCHIVED)
export function useArchiveProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => archiveProject(id),
    onSuccess: async () => {
      // Refetch all project queries to update the list
      await queryClient.refetchQueries({ queryKey: ['projects'] })
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to archive project')
    },
  })
}

// Delete project (soft delete)
export function useDeleteProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteProject(id),
    onSuccess: async (_, id) => {
      queryClient.removeQueries({ queryKey: ['projects', id] })
      // Use refetchQueries() to wait for fresh data before UI updates
      await queryClient.refetchQueries({ queryKey: ['projects'] })
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to delete project')
    },
  })
}

// Duplicate project
export function useDuplicateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      duplicateProject(id, name),
    onSuccess: async () => {
      // Use refetchQueries() to wait for fresh data before UI updates
      await queryClient.refetchQueries({ queryKey: ['projects'] })
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to duplicate project')
    },
  })
}
